import { portcoUpdatesApi } from "@/api/portco-updates-api";
import { submissionApiClient } from "@/api/submission-api";
import type { Workspace } from "@/api/workspace-api";
import { Button } from "@/components/ui/button";
import { GoogleDrivePicker } from "../google-drive-picker";
import { getErrorMessage } from "@/lib/api-error";
import { getUrlValidationError } from "@/lib/url-utils";
import {
  StepCard as Card,
  StepCardContent as CardContent,
  StepCardDescription as CardDescription,
  StepCardHeader as CardHeader,
  StepCardTitle as CardTitle,
} from "../portal-ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle,
  Upload,
  FileText,
  Trash2,
  Loader2,
  Plus,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { FILE_TYPE_MAPPING } from "@/lib/utils";
import { toast } from "sonner";

interface PortcoUpdateStepProps {
  opportunityData: Workspace;
  companyData: any;
  onBack?: () => void;
  onComplete: () => void;
}

interface OpportunityQuestion {
  key: string;
  title: string;
  helper_text?: string;
  required?: boolean;
  display?: boolean;
}

export default function PortcoUpdateStep({
  opportunityData,
  companyData,
  onBack,
}: PortcoUpdateStepProps) {
  const { user } = useUser();
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [questions, setQuestions] = useState<OpportunityQuestion[]>([]);

  // File upload state
  const [showAddFileDialog, setShowAddFileDialog] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<
    Array<{
      file: File;
      fileType: string;
      fileName: string;
    }>
  >([]);
  const [driveFiles, setDriveFiles] = useState<
    Array<{
      _id: string;
      file_type: string;
      file_url?: string;
      file_name?: string;
    }>
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isGoogleDriveOpen, setIsGoogleDriveOpen] = useState(false);

  useEffect(() => {
    console.log("opportunityData", opportunityData);
    console.log("companyData", companyData);

    // Initialize form data with company info
    if (companyData && companyData._id) {
      const initialData: Record<string, string> = {};
      Object.keys(companyData).forEach((key) => {
        if (typeof companyData[key] === "string") {
          initialData[key] = companyData[key] || "";
        }
      });
      setFormData(initialData);
    }

    // Parse portco form questions
    if (
      opportunityData?.portco_form &&
      Object.keys(opportunityData.portco_form).length > 0
    ) {
      const questionsArray = Object.keys(opportunityData.portco_form).map(
        (key) => ({
          key,
          ...opportunityData.portco_form[key],
        })
      );
      setQuestions(questionsArray);
    } else {
      setQuestions([]);
    }
  }, [companyData, opportunityData]);

  const handleInputChange = (questionKey: string, value: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [questionKey]: value,
    }));

    // Clear error when user starts typing
    if (errors[questionKey]) {
      setErrors((prev) => ({ ...prev, [questionKey]: "" }));
    }
  };

  const handleFileSelection = (files: FileList | null) => {
    if (!files?.length) return;
    setSelectedFile(files[0]);
  };

  const handleAddFile = () => {
    if (!selectedFile || !selectedFileType) return;

    const newFile = {
      file: selectedFile,
      fileType: selectedFileType,
      fileName: selectedFile.name,
    };

    setUploadedFiles((prev) => [...prev, newFile]);

    // Reset form and close dialog
    setSelectedFile(null);
    setSelectedFileType("");
    setShowAddFileDialog(false);

    toast.success("File added successfully");
  };

  const handleAddFileDialogClose = () => {
    setShowAddFileDialog(false);
    setSelectedFile(null);
    setSelectedFileType("");
  };

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeDriveFile = (index: number) => {
    setDriveFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGoogleDriveImport = (importedFiles: any[]) => {
    const newDriveFiles = importedFiles.map((f) => ({
      _id: f._id,
      file_type: f.file_type || "other",
      file_url: f.file_url,
      file_name: f.name || "Google Drive File",
    }));

    setDriveFiles((prev) => [...prev, ...newDriveFiles]);
    setIsGoogleDriveOpen(false);
  };

  const GoogleDriveIcon = ({ className }: { className?: string }) => (
    <svg
      className={className}
      viewBox="0 0 87.3 78"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
        fill="#0066da"
      />
      <path
        d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
        fill="#00ac47"
      />
      <path
        d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
        fill="#ea4335"
      />
      <path
        d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
        fill="#00832d"
      />
      <path
        d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
        fill="#2684fc"
      />
      <path
        d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
        fill="#ffba00"
      />
    </svg>
  );

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate required fields
    if (!formData.update_title || formData.update_title.trim() === "") {
      newErrors.update_title = "Update title is required";
    }

    if (
      !formData.update_description ||
      formData.update_description.trim() === ""
    ) {
      newErrors.update_description = "Update description is required";
    }

    questions.forEach((question) => {
      if (question.display && question.required) {
        if (!formData[question.key] || formData[question.key].trim() === "") {
          newErrors[question.key] = "This field is required";
        }
      }
    });

    // Use ?? so an intentional empty string in formData (user cleared the
    // field) isn't silently replaced by a stale companyData value, which
    // would cause the validator to flag or pass the wrong input.
    const websiteValue =
      formData.company_website ?? companyData?.company_website ?? "";
    const websiteError = getUrlValidationError(websiteValue);
    if (websiteError) newErrors.company_website = websiteError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    // Validate user is logged in
    if (!user || !user.id) {
      setErrors({
        submit:
          "You must be logged in to submit an update. Please go back and login.",
      });
      return;
    }

    // Validate company data before submitting
    if (!companyData || !companyData._id) {
      console.error("Company data is missing or invalid:", companyData);
      setErrors({
        submit:
          "Company information is missing. Please go back and complete the previous steps.",
      });
      return;
    }

    setIsSubmitting(true);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      console.log(
        "Submitting portfolio company update with company ID:",
        companyData._id
      );
      console.log("Submitting with user ID:", user.id);

      // Extract company info fields that should be updated in the Company model
      // These are fields that users can edit in the portco update form
      const companyInfoFields: Record<string, any> = {};

      // Check if company_website has changed and needs to be updated
      if (
        formData.company_website !== undefined &&
        formData.company_website !== null &&
        formData.company_website !== "" &&
        formData.company_website !== companyData?.company_website
      ) {
        companyInfoFields.companyWebsite = formData.company_website;
      }

      // Update company info if there are changes
      if (Object.keys(companyInfoFields).length > 0) {
        console.log(
          "Updating company info before portco update:",
          companyInfoFields
        );
        try {
          await submissionApiClient.company.updateCompany(
            companyData._id,
            companyInfoFields as any
          );
          console.log("Company info updated successfully");
        } catch (error) {
          console.error("Error updating company info:", error);
          // Don't fail the whole submission if company update fails, but log it
          toast.error(
            getErrorMessage(
              error,
              "Company info update failed, but continuing with portco update submission"
            )
          );
        }
      }

      // Prepare portco update info from questions
      const portcoUpdateInfo: Record<string, string> = {};
      questions.forEach((question) => {
        if (formData[question.key]) {
          portcoUpdateInfo[question.key] = formData[question.key];
        }
      });

      // Prepare files and file types
      const files = uploadedFiles.map((item) => item.file);
      const fileTypes = uploadedFiles.map((item) => item.fileType);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 5;
        });
      }, 100);

      // Submit portco update
      const response = await portcoUpdatesApi.submitUpdate(
        opportunityData._id,
        companyData._id,
        {
          update_title: formData.update_title,
          update_description: formData.update_description,
          portco_update: portcoUpdateInfo,
          files,
          file_types: fileTypes,
          drive_files: driveFiles,
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.status === "success") {
        toast.success("Portfolio company update submitted successfully");
        setShowSuccessModal(true);
      } else {
        throw new Error(response.message || "Failed to submit update");
      }
    } catch (error) {
      console.error("Error submitting portco update:", error);
      setErrors({
        submit: "Failed to submit portfolio company update. Please try again.",
      });
      toast.error(
        getErrorMessage(error, "Failed to submit portfolio company update")
      );
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    const baseUrl =
      import.meta.env.VITE_STARTUPFUEL_APP_URL ||
      "https://app.startupfuel.com";
    window.location.href = `${baseUrl}/funding?redirect=/funding`;
  };

  const getQuestionLabel = (question: OpportunityQuestion) => {
    return (
      <span>
        {question.title}
        {question.required && <span className="text-destructive ml-1">*</span>}
      </span>
    );
  };

  return (
    <div className="mx-auto w-full max-w-2xl px-5 sm:px-8">
      <Card>
        <CardHeader>
          <CardTitle>Portfolio update</CardTitle>
          <CardDescription>
            Bring your company details up to date and answer this period&rsquo;s
            questions. Your update goes to the team for review.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Company Information Section */}
          <div className="space-y-4">
            <h3 className="text-[15px] font-semibold tracking-tight text-foreground">
              Company Information
            </h3>

            {/* Basic Company Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <input
                  id="companyName"
                  type="text"
                  disabled={true}
                  value={
                    formData.company_name || companyData?.company_name || ""
                  }
                  onChange={(e) =>
                    handleInputChange("company_name", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Enter company name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyWebsite">Company Website</Label>
                <input
                  id="companyWebsite"
                  type="url"
                  value={
                    formData.company_website ||
                    companyData?.company_website ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChange("company_website", e.target.value)
                  }
                  onBlur={(e) => {
                    const err = getUrlValidationError(e.target.value);
                    setErrors((prev) => ({
                      ...prev,
                      company_website: err ?? "",
                    }));
                  }}
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="https://example.com"
                />
                {errors.company_website && (
                  <p className="text-sm text-destructive">
                    {errors.company_website}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-[15px] font-semibold tracking-tight text-foreground">
                Company Update
              </h3>
              <div className="space-y-2">
                <Label htmlFor="updateTitle">Update Title</Label>
                <input
                  id="updateTitle"
                  type="text"
                  value={formData.update_title || ""}
                  onChange={(e) =>
                    handleInputChange("update_title", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Enter a title for this update"
                />
                {errors.update_title && (
                  <p className="text-sm text-destructive">{errors.update_title}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="updateDescription">Update Description</Label>
                <Textarea
                  id="updateDescription"
                  value={formData.update_description || ""}
                  onChange={(e) =>
                    handleInputChange("update_description", e.target.value)
                  }
                  placeholder="Describe the details of this company update"
                  className="min-h-[100px]"
                />
                {errors.update_description && (
                  <p className="text-sm text-destructive">
                    {errors.update_description}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Document Upload Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-[15px] font-semibold tracking-tight text-foreground">
                Documents & Files
              </h3>
              <Button
                onClick={() => setShowAddFileDialog(true)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add File
              </Button>
            </div>

            <div className="flex gap-2 mb-4">
              <Button
                variant="outline"
                onClick={() => setIsGoogleDriveOpen(true)}
                className="gap-2"
                disabled={isSubmitting || isUploading}
              >
                <div className="h-4 w-4">
                  <GoogleDriveIcon />
                </div>
                Import from Google Drive
              </Button>
            </div>

            {/* Display uploaded files */}
            {uploadedFiles.length > 0 || driveFiles.length > 0 ? (
              <div className="space-y-2">
                <h4 className="font-medium text-foreground/80">Files to Upload:</h4>
                <div className="space-y-2">
                  {/* Local Files */}
                  {uploadedFiles.map((fileItem, index) => {
                    // Helper to truncate file names
                    const truncateFileName = (name: string, maxLength = 25) => {
                      if (name.length <= maxLength) return name;
                      const extIndex = name.lastIndexOf(".");
                      if (extIndex === -1 || extIndex < name.length - 8) {
                        // No extension or extension is long, just truncate
                        return name.slice(0, maxLength - 3) + "...";
                      }
                      const ext = name.slice(extIndex);
                      const base = name.slice(0, maxLength - ext.length - 3);
                      return base + "..." + ext;
                    };

                    return (
                      <div
                        key={`local-${fileItem.fileName}-${index}`}
                        className="flex items-center justify-between rounded-lg border border-border bg-card p-3"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <Upload className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <span
                              className="text-sm font-medium text-foreground truncate max-w-[200px] sm:max-w-[250px] block"
                              title={fileItem.fileName}
                            >
                              {truncateFileName(fileItem.fileName)}
                            </span>
                            <div className="text-xs text-muted-foreground">
                              Type: {fileItem.fileType} • Local Upload
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/5"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}

                  {/* Drive Files */}
                  {driveFiles.map((fileItem, index) => (
                    <div
                      key={`drive-${fileItem._id}-${index}`}
                      className="flex items-center justify-between rounded-lg border border-border bg-card p-3"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="h-5 w-5 flex-shrink-0">
                          <GoogleDriveIcon />
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <span
                            className="text-sm font-medium text-foreground truncate max-w-[200px] sm:max-w-[250px] block"
                            title={fileItem.file_name}
                          >
                            {fileItem.file_name}
                          </span>
                          <div className="text-xs text-muted-foreground">
                            Type: {fileItem.file_type} • Google Drive
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDriveFile(index)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/5"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground border-[1.5px] border-dashed border-border rounded-xl">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-sm">No files added yet</p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Click "Add File" to upload documents
                </p>
              </div>
            )}

            {/* Upload Progress */}
            {isUploading && (
              <div className="space-y-2">
                <Progress value={uploadProgress} />
                <p className="text-sm text-center text-muted-foreground">
                  Uploading... {uploadProgress}%
                </p>
              </div>
            )}
          </div>

          {/* Application Questions Section */}
          <div className="space-y-4">
            <h3 className="text-[15px] font-semibold tracking-tight text-foreground">
              Additional Information
            </h3>

            {questions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <p>No additional questions for this opportunity.</p>
              </div>
            ) : (
              questions
                .filter((question) => question.display)
                .map((question) => (
                  <div key={question.key} className="space-y-2">
                    <Label htmlFor={question.key}>
                      {getQuestionLabel(question)}
                    </Label>
                    <Textarea
                      id={question.key}
                      value={formData[question.key] || ""}
                      onChange={(e) =>
                        handleInputChange(question.key, e.target.value)
                      }
                      placeholder={
                        question.helper_text || "Enter your response..."
                      }
                      className="min-h-[100px]"
                    />
                    {errors[question.key] && (
                      <p className="text-sm text-destructive">
                        {errors[question.key]}
                      </p>
                    )}
                  </div>
                ))
            )}
          </div>

          {/* Submission Error */}
          {errors.submit && (
            <div className="p-3 bg-destructive/5 border border-destructive/30 rounded-md flex items-center gap-2">
              <span className="text-destructive">⚠️</span>
              <p className="text-sm text-destructive">{errors.submit}</p>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6 pb-2 mt-4 border-t border-border/70">
            {onBack && (
              <Button type="button" variant="outline" onClick={onBack}>
                Back
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || isUploading}
              className="w-full sm:w-auto"
            >
              {isSubmitting || isUploading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isUploading ? "Uploading Files..." : "Submitting Update..."}
                </div>
              ) : (
                "Submit Portfolio Company Update"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Add File Dialog */}
      <Dialog open={showAddFileDialog} onOpenChange={setShowAddFileDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-muted-foreground" />
              Add File
            </DialogTitle>
            <DialogDescription>
              Select the file type and upload your document
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="file-type">File Type</Label>
              <Select
                onValueChange={setSelectedFileType}
                value={selectedFileType}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select file type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(FILE_TYPE_MAPPING)
                    .filter((label) => !["CSV", "XLSX"].includes(label))
                    .map((label) => (
                      <SelectItem
                        key={label}
                        value={
                          FILE_TYPE_MAPPING[
                          label as keyof typeof FILE_TYPE_MAPPING
                          ]
                        }
                      >
                        {label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="file-upload">File</Label>
              <div
                className="rounded-xl border-[1.5px] border-dashed border-input p-6 text-center transition-colors duration-150 hover:border-foreground/40 hover:bg-card"
                onDrop={(e) => {
                  e.preventDefault();
                  handleFileSelection(e.dataTransfer.files);
                }}
                onDragOver={(e) => e.preventDefault()}
              >
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={(e) => handleFileSelection(e.target.files)}
                />
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center cursor-pointer"
                >
                  <Upload className="h-8 w-8 mb-3 text-muted-foreground/70" />
                  {selectedFile ? (
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-foreground underline underline-offset-4">
                        {selectedFile.name.length > 30
                          ? selectedFile.name.substring(0, 27) + "..."
                          : selectedFile.name}
                      </span>
                      <p className="text-xs text-muted-foreground">
                        Click to change file
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <span className="text-sm text-muted-foreground">
                        Drag and drop or click to upload
                      </span>
                      <p className="text-xs text-muted-foreground/70">
                        PDF, DOC, XLS, PPT, etc.
                      </p>
                    </div>
                  )}
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={handleAddFileDialogClose}>
              Cancel
            </Button>
            <Button
              onClick={handleAddFile}
              disabled={!selectedFile || !selectedFileType}
            >
              Add File
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-8 h-8 text-raising" />
              <DialogTitle>Update Submitted!</DialogTitle>
            </div>
            <DialogDescription className="text-left">
              Your portfolio company update has been successfully submitted for{" "}
              <strong>{opportunityData.opportunity_name}</strong>.
              <br />
              <br />
              Our team will review your updated information and get back to you
              if needed.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-6">
            <Button onClick={handleSuccessClose} className="w-full">
              My Applications
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Google Drive Picker */}
      <GoogleDrivePicker
        open={isGoogleDriveOpen}
        onClose={() => setIsGoogleDriveOpen(false)}
        onImportComplete={handleGoogleDriveImport}
        companyId={companyData?._id || ""}
        opportunityId={opportunityData._id}
      />
    </div>
  );
}
